import React, { useState } from 'react';
import { MemberModel, ScheduleEvent, SiteSettingModel, ScheduleMarker } from '../../../../models';
import { Settings2, Plus, X } from 'lucide-react';
import ScheduleRangeManagement from './ScheduleRangeManagement';
import ScheduleEditManagement from './ScheduleEditManagement';

interface ScheduleManagerProps {
    member: MemberModel;
    siteSettings: SiteSettingModel;
    activeSchedules: ScheduleEvent[];
    recycleBinSchedules: ScheduleEvent[];
    scheduleMarkers?: ScheduleMarker[];
    initialStartDate?: string;
    initialEndDate?: string;
    onClose: () => void;
    onSave: (events: ScheduleEvent[]) => void;
    onDelete: (ids: string[]) => void;
    onRestore: (ids: string[]) => void;
    onPermanentDelete: (ids: string[]) => void;
    onSaveMarker?: (marker: ScheduleMarker) => void;
    onDeleteMarker?: (id: string) => void;
}

type ModalMode = 'list' | 'create';


const ScheduleManager: React.FC<ScheduleManagerProps> = ({
    member, siteSettings, activeSchedules, recycleBinSchedules, scheduleMarkers = [],
    initialStartDate, initialEndDate,
    onClose, onSave, onDelete, onRestore, onPermanentDelete,
    onSaveMarker, onDeleteMarker
}) => {
    // Mode State
    const [mode, setMode] = useState<ModalMode>('list');

    return (
        <div className="flex flex-col min-h-full bg-slate-50">

            {/* Header */}
            <header className="sticky top-0 z-40 bg-white p-6 border-b border-slate-100 flex items-center justify-between shrink-0 shadow-sm">
                <div className="flex items-center gap-4">
                    <div className={`w-12 h-12 rounded-2xl flex items-center justify-center text-white shadow-lg ${mode === 'create' ? 'bg-indigo-600 shadow-indigo-100' : 'bg-slate-800 shadow-slate-200'}`}>
                        {mode === 'create' ? <Plus size={24} /> : <Settings2 size={24} />}
                    </div>
                    <div>
                        <h3 className="text-xl sm:text-2xl font-black text-slate-800 tracking-tight">
                            {mode === 'create' ? '일정 추가' : '일정 관리자'}
                        </h3>
                        <p className="text-xs sm:text-sm text-slate-400 font-medium">
                            {mode === 'create' ? '새로운 학습 일정을 생성합니다.' : '기간별 일정 조회, 삭제 및 복구 관리'}
                        </p>
                    </div>
                </div>
                <button onClick={onClose} className="w-12 h-12 flex items-center justify-center bg-slate-50 text-slate-400 rounded-2xl hover:bg-slate-100 transition-all">
                    <X size={24} />
                </button>
            </header>

            {/* Content */}
            <div className="flex-1 flex flex-col">
                {mode === 'list' ? (
                    <ScheduleRangeManagement
                        activeSchedules={activeSchedules}
                        recycleBinSchedules={recycleBinSchedules}
                        scheduleMarkers={scheduleMarkers}
                        initialStartDate={initialStartDate}
                        initialEndDate={initialEndDate}
                        onDelete={onDelete}
                        onRestore={onRestore}
                        onPermanentDelete={onPermanentDelete}
                        onDeleteMarker={onDeleteMarker}
                        onSwitchToCreate={() => setMode('create')}
                    />
                ) : (
                    <ScheduleEditManagement
                        member={member}
                        siteSettings={siteSettings}
                        scheduleMarkers={scheduleMarkers}
                        initialStartDate={initialStartDate}
                        initialEndDate={initialEndDate}
                        onSave={onSave}
                        onSaveMarker={onSaveMarker}
                        onCancel={() => setMode('list')}
                    />
                )}
            </div>
        </div>
    );
};

export { ScheduleManager };
